import { distinctUntilChanged, filter, catchError, tap, map, switchMap } from 'rxjs/operators';
import { Injectable, Inject, NgZone } from '@angular/core';
import { CanActivate, Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, of, race } from 'rxjs';

import { errorSelectors } from '../selectors';
import { fromError } from '../reducers';
import { WebAngularSharedUtilErrorHandlingOptions, WebAngularSharedUtilErrorHandlingConfig } from '../error-handling-config';

@Injectable()
export class GenericErrorGuard implements CanActivate {
  constructor(
    protected store: Store<fromError.State>,
    protected router: Router,
    private zone: NgZone,
    @Inject(WebAngularSharedUtilErrorHandlingConfig) private errorHandlingOptions: WebAngularSharedUtilErrorHandlingOptions = {}
  ) {}

  canActivate(): Observable<boolean> {
    return this.checkIfNoError().pipe(
      catchError(() => of(false)),
      tap(noError => {
        if (!noError && !this.errorHandlingOptions.doNotRedirectOnErrorGuard) {
          this.goToErrorRoute();
        }
      })
    );
  }

  protected waitUntil(): Observable<boolean> {
    return of(true);
  }
  /**
   * Checks to see if there are errors after whichever comes first: the waitUntil() function completes, or more errors are added
   *
   * @returns {Observable<boolean>}
   * @memberof GenericErrorGuard
   */
  checkIfNoError(): Observable<boolean> {
    return race(
      this.waitUntil().pipe(filter(waitDone => waitDone)),
      this.store.pipe(
        select(errorSelectors.getErrorCount),
        distinctUntilChanged(),
        map(numErrors => numErrors > 0),
        filter(hasErrors => hasErrors)
      )
    ).pipe(
      switchMap(() =>
        this.store.pipe(
          select(errorSelectors.getHasError),
          map(hasError => !hasError)
        )
      )
    );
  }

  /**
   * Goes to the error route (/error) unless otherwise set by the options
   *
   * @memberof GenericErrorGuard
   */
  goToErrorRoute() {
    // Changing route in error handling: https://github.com/angular/angular/issues/15946
    this.zone.run(() =>
      this.router.navigate([this.errorHandlingOptions.errorRedirectRoute || '/error'])
    );
  }
}
